/**
 * The only place this app talks to the API.
 *
 * One function, because every call needs the same three things and getting any
 * of them wrong is silent. Credentials must be included or the session cookie
 * never travels. A non-2xx must become a typed error rather than a rejected
 * promise carrying a status number, because `CLAUDE.md` says a user never sees
 * a status code and the surest way to honour that is to never have one in hand.
 *
 * The server sends `{ error: { code, message, diagnostic } }` and the code is
 * what the UI maps to themed copy through `errorCopyFor()`. The message that
 * arrives is a fallback for anything the client does not recognise, never the
 * primary source.
 */

import type { ErrorCode } from '@good-season/schema';

import { clearSignOutPending, signOutPending } from '../scorebook/db.js';
import { notifyApiFailure } from './failure-hook.js';

export class ApiError extends Error {
  override readonly name = 'ApiError';
  readonly code: ErrorCode;
  readonly diagnostic: string;
  readonly status: number;
  /**
   * The field names a validation failure named, empty for every other code.
   *
   * A set rather than the server's `{ field, code }` pairs, because the issue
   * code is the server's vocabulary and a form must not render it. What crosses
   * this boundary is WHICH box, never WHY in the server's words.
   */
  readonly fields: ReadonlySet<string>;

  constructor(status: number, code: ErrorCode, diagnostic: string, fields: readonly string[] = []) {
    // The message is for a developer reading a console, never for a screen.
    super(`${code} (${diagnostic})`);
    this.status = status;
    this.code = code;
    this.diagnostic = diagnostic;
    this.fields = new Set(fields);
  }
}

/**
 * Thrown when no answer arrived, which is a different story to tell.
 *
 * It does NOT say the request never reached the server: a fetch rejects the
 * same way when the connection drops after the server committed and before
 * its answer arrived. Only `neverSent` says the request did not leave.
 */
export class OfflineError extends Error {
  override readonly name = 'OfflineError';
  /**
   * True only when the `timeoutMs` deadline fired; a fetch that rejects with no
   * network leaves it false. The scorebook sync reads this to shrink its batch.
   */
  readonly timedOut: boolean;
  /**
   * True only when the caller knew the device was offline (the platform
   * network adapter said so) and never issued the request, so it cannot have
   * reached the server. Every OfflineError this module throws leaves it false.
   */
  readonly neverSent: boolean;

  constructor(message: string, timedOut = false, neverSent = false) {
    super(message);
    this.timedOut = timedOut;
    this.neverSent = neverSent;
  }
}

interface ErrorEnvelope {
  readonly error?: {
    readonly code?: string;
    readonly diagnostic?: string;
    readonly fields?: readonly { readonly field?: unknown }[];
  };
}

/**
 * The root in production, and behind the vite proxy in development.
 *
 * Callers pass `/api/team` and this puts it where the API actually is. Doing it
 * here rather than at each call site means there is one place the client and
 * the server's `basePath` can disagree, and both read it from the same vite
 * `base`.
 */
const API_ROOT = import.meta.env.BASE_URL.replace(/\/$/, '');

/**
 * The team a request should act as, if the page has chosen one.
 *
 * `useTeams()` (`apps/web/src/team/useTeams.ts`) writes `?team=` on the page
 * when somebody switches teams, and this reads the same string back at the
 * moment a request is made. `location.search`, not `useSearchParams()`: this
 * module is a plain function, not a component, and has nothing to hook a
 * subscription into. The two read the same underlying object regardless,
 * since `BrowserRouter`'s history is itself backed by the browser's location,
 * so there is nothing here for the two to disagree about.
 *
 * `location`, unprefixed, is a routing concern rather than one of the nine
 * platform capabilities `MOBILE_SUPPLEMENT.md` 2.1 lists and ADR-0021 scopes
 * that section to. It matches how `SignIn.tsx` reads `?token=` today, just
 * from outside a component.
 */
function selectedTeamId(): string | null {
  /*
   * An EMPTY value is no selection, not a selection of nothing.
   *
   * `URLSearchParams.get` answers '' for `?team=`, which is not null, so the
   * funnel appended `teamId=` and the server saw a string it could match to no
   * membership and refused every request. `?team=` then stuck, because the nav
   * links carry whatever is there. Found by the fresh-context pass.
   */
  const value = new URLSearchParams(location.search).get('team');
  return value === null || value === '' ? null : value;
}

/**
 * `?teamId=` (or `&teamId=`, for a path that already carries a query string),
 * appended here so every call through `api()` carries it rather than relying
 * on a dozen call sites across the app to remember. Absent a selection, the
 * path is returned unchanged and `requireSession` on the server falls back to
 * its own default (`apps/api/src/http/tenant.ts`), which is what "let the
 * server choose" means end to end.
 */
/**
 * The one call that must NOT carry the selection: the list of teams.
 *
 * Scoping it would make a stale selection unrecoverable. `requireSession`
 * refuses a `teamId` the person does not hold, so a bookmarked link to a team
 * they have since been removed from would 403 the very endpoint the switcher
 * needs to offer them a team they DO hold. The switcher would vanish at exactly
 * the moment it is the way back in.
 *
 * `membership.ts` already records this shape happening once, where a reserved
 * role made every unscoped request 501 "INCLUDING /api/team/memberships, which
 * is the endpoint you would use to find the other team. The user was locked out
 * with no way to discover the way back in." Graded P2 by the cross-model pass.
 *
 * Compared on the path before any query string, so a caller adding parameters
 * later cannot accidentally re-scope it.
 */
const UNSCOPED = '/api/team/memberships';

/**
 * Every route under the admin dashboard, which is the identical hazard one
 * level up: `admin.ts` never reads the single membership `requireSession`
 * resolves, so appending a stale or foreign `?teamId=` would only buy a 403
 * from the MIDDLEWARE, before the dashboard's own cross-team logic ever runs,
 * for a screen whose entire purpose is to work regardless of which team is
 * selected. A prefix rather than one literal path, because every sub-route
 * here (the overview and all three resend actions) shares the same reason.
 */
const UNSCOPED_PREFIX = '/api/admin/';
/**
 * A help report must not carry `?teamId=`. That id joins to a roster, and the
 * report is not allowed to attach one (issue #175).
 */
const UNSCOPED_REPORT = '/api/support-reports';

function withTeamId(path: string): string {
  const teamId = selectedTeamId();
  if (teamId === null) return path;
  const withoutQuery = path.split('?')[0] ?? path;
  if (
    withoutQuery === UNSCOPED ||
    withoutQuery === UNSCOPED_REPORT ||
    withoutQuery.startsWith(UNSCOPED_PREFIX)
  ) {
    return path;
  }
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}teamId=${encodeURIComponent(teamId)}`;
}

/*
 * A sign-out with no signal (#43, ADR-0090).
 *
 * The session cookie is HttpOnly, so tapping Sign out in a gym ends nothing:
 * the server still holds the session and the next request carries it. So the
 * phone records the sign-out (`forgetSignedIn` writes it beside the null owner
 * record, and `noteSignOut` keeps a copy on this page), and every request here
 * sends it first.
 *
 * THE INVARIANT. A non-auth request is sent only after the code has positively
 * observed that no sign-out is waiting on this page and in the store, or after
 * a sign-out landed with a 2xx. Fail closed: whatever the code cannot
 * establish counts as a sign-out waiting, never as none. A store read that
 * throws or passes its deadline is "unknown": the request waits and the store
 * is read again, up to STORE_READ_ATTEMPTS reads, so a slow cold open costs
 * time, not a refusal. A check that began before the latest `noteSignOut`
 * decides nothing. The store is read before EVERY request, so a sign-out
 * written by another tab is seen by this one. The one bounded way past an
 * unknown: when every one of those reads goes unanswered the page sends the
 * sign-out itself, and once that lands its own unknowns no longer block,
 * because the session it ended was the one in doubt, until a sign-in on this
 * page starts a new session that another tab could end. A request whose
 * sign-out cannot be sent, or whose own `timeoutMs` runs out first, answers
 * AUTH_REQUIRED (diagnostic SGNOUT).
 */
const SIGN_OUT = '/api/auth/signout';
/** The paths that start a session. A 2xx from one makes this page's unknown reads block again. */
const SIGN_INS = new Set([
  '/api/auth/callback',
  '/api/auth/invite',
  '/api/auth/team-invite/redeem',
  '/api/auth/review-signin',
  '/api/auth/password',
  '/api/auth/password/set',
]);

/**
 * How long the sign-out itself may take before this attempt gives up and the
 * next request tries again. Every other request on the page waits behind it,
 * so it must end: a tiny POST, given the box score's fixture read deadline
 * (8 s, ADR-0085), and inside the sync's 15 s so a sync tick still has time
 * left to send once the sign-out lands. A caller's own `timeoutMs` covers the
 * wait too, so a shorter deadline is never stretched by this one.
 */
export const SIGN_OUT_TIMEOUT_MS = 8_000;

/**
 * How long a read or a clear of the stored flag may take. A healthy one is a
 * few milliseconds; an IndexedDB open that never resolves (seen on iOS WebKit)
 * must end, and ends as "unknown".
 */
export const STORE_READ_TIMEOUT_MS = 2_000;

/** Unanswered store reads one check makes, back to back, before the page ends the unknown session itself. */
export const STORE_READ_ATTEMPTS = 3;

/** Every sign-out on this page: the generation a check belongs to. */
let signOuts = 0;
/** A sign-out noted on this page that has not landed. */
let unsentHere = false;
/** The store has said a sign-out is waiting since this page noted one, so its "none" now means another page sent it. */
let storeHeld = false;
/** A sign-out landed with a 2xx from this page since it last noted one. */
let landed = false;
let checking: Promise<void> | null = null;
/** A sign-out was noted on this page and nobody has been recorded signing in here since: no book opens here (#43). */
let signedOutHere = false;

export const signOutCount = (): number => signOuts;

/** Whether this page has signed out since a sign-in was last recorded here, whatever the store still says. */
export const signedOutOnThisPage = (): boolean => signedOutHere;

/**
 * Called once the store has recorded who signed in on this page, never on a
 * sign-in 2xx alone: until then the store may still name whoever signed out.
 */
export function noteSignedInRecorded(): void {
  signedOutHere = false;
}

/** Called by sign-out, before anything else it does. */
export function noteSignOut(): void {
  signOuts += 1;
  signedOutHere = true;
  unsentHere = true;
  storeHeld = false;
  landed = false;
  // A check already in flight looked before this sign-out existed.
  checking = null;
}

/** What `work` answers, or undefined when it fails or has not answered within STORE_READ_TIMEOUT_MS. */
export async function bounded<T>(work: Promise<T>): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      work,
      new Promise<undefined>((resolve) => {
        timer = setTimeout(resolve, STORE_READ_TIMEOUT_MS, undefined);
      }),
    ]);
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}

/** One check for generation `g`. Resolves when nothing is waiting, or when a newer sign-out makes it moot. */
async function check(g: number): Promise<void> {
  let stored: boolean | undefined;
  // An unknown read waits and reads again: a cold IndexedDB open can outlast one bound.
  for (let read = 1; ; read += 1) {
    stored = await bounded(signOutPending());
    if (g !== signOuts) return;
    if (stored !== undefined || unsentHere || landed || read >= STORE_READ_ATTEMPTS) break;
  }
  if (stored === undefined) {
    if (!unsentHere && landed) return;
    // Otherwise this page's copy is the record, or the reads ran out: send the sign-out.
  } else {
    if (stored) storeHeld = true;
    else if (!unsentHere) return;
    else if (storeHeld) {
      // The store held it and is now clear: another page landed it, and may have signed in since.
      unsentHere = false;
      storeHeld = false;
      return;
    }
    // Otherwise the store never took this page's write, and this page's copy is the record.
  }
  const deadline = new AbortController();
  const timer = setTimeout(() => deadline.abort(), SIGN_OUT_TIMEOUT_MS);
  try {
    await send(SIGN_OUT, { method: 'POST', body: '{}' }, deadline.signal, deadline.signal);
  } finally {
    clearTimeout(timer);
  }
  if (g !== signOuts) return;
  unsentHere = false;
  storeHeld = false;
  landed = true;
  // Outside the shared check, so a store that hangs on the delete holds nothing up.
  // shortcut: a store that never manages this delete re-sends the sign-out on every later request, ending whoever signed in since; upgrade if such a store is ever seen.
  void bounded(clearSignOutPending());
}

/**
 * Send a recorded sign-out, if there is one. Resolves once the code has seen
 * that none is waiting, rejects while it cannot (the store did not answer, or
 * the server could not be told). Only a 2xx confirms a sign-out: the API
 * answers one with 204 whether or not a session was live, so a 401 or anything
 * else came from a portal or a proxy.
 */
export async function sendSignOut(): Promise<void> {
  let g: number;
  do {
    g = signOuts;
    if (checking === null) {
      const mine: Promise<void> = check(g).finally(() => {
        if (checking === mine) checking = null;
      });
      checking = mine;
    }
    await checking;
    // A sign-out noted while that check was out: check again for it.
  } while (g !== signOuts);
}

/** `work`, or an `OfflineError` the moment `deadline` fires, whichever is first. */
function within(work: Promise<unknown>, deadline: AbortSignal | undefined): Promise<unknown> {
  if (deadline === undefined) return work;
  return Promise.race([
    work,
    new Promise<never>((_, reject) => {
      deadline.addEventListener(
        'abort',
        () => {
          reject(new OfflineError('the request timed out', true));
        },
        { once: true },
      );
    }),
  ]);
}

export interface ApiOptions {
  /**
   * Give up on the request after this long and throw `OfflineError`. Absent, the
   * request waits as long as the browser lets it. Set by the scorebook sync
   * (its single-flight guard turns one unanswered request into a stuck strip)
   * and by the box score screen's reads (a stalled load must end in its
   * designed offline state, because an installed PWA has no browser chrome to
   * leave a loading screen with). Any read that can otherwise leave a screen
   * waiting without a resolution should set it, and that screen keeps a working
   * exit while it waits (ADR-0085).
   */
  readonly timeoutMs?: number | undefined;
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
  options: ApiOptions = {},
): Promise<T> {
  /*
   * A controller and a timer rather than `AbortSignal.timeout`, so the timer is
   * cleared the moment the request settles (no stray timer per call) and so a
   * test can drive it with fake timers. A caller's own signal, if it passed
   * one, still aborts the request: the two are composed with `AbortSignal.any`.
   * Started before the sign-out below, so the deadline covers the wait for it.
   */
  const timeout = options.timeoutMs === undefined ? null : new AbortController();
  const timer =
    timeout === null || options.timeoutMs === undefined
      ? null
      : setTimeout(() => timeout.abort(), options.timeoutMs);
  const signal =
    timeout === null
      ? init.signal
      : init.signal
        ? AbortSignal.any([init.signal, timeout.signal])
        : timeout.signal;

  try {
    const auth = path.startsWith('/api/auth/');
    try {
      await within(sendSignOut(), timeout?.signal);
    } catch (error) {
      // A sign-in screen tells its own story (no signal, a fault); everything else is signed out.
      if (auth) throw error;
      throw new ApiError(401, 'AUTH_REQUIRED', 'SGNOUT');
    }
    const answer = await send<T>(path, init, signal ?? undefined, timeout?.signal);
    // A new session: the one this page ended is no longer the one in doubt.
    if (SIGN_INS.has(path.split('?')[0] ?? path)) {
      landed = false;
    }
    return answer;
  } finally {
    if (timer !== null) clearTimeout(timer);
  }
}

async function send<T>(
  path: string,
  init: RequestInit,
  signal: AbortSignal | undefined,
  timedOut: AbortSignal | undefined,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_ROOT}${withTeamId(path)}`, {
      ...init,
      // AFTER the spread, not before.
      //
      // Spreading `init` last let any caller passing `headers` replace the
      // merged object wholesale and lose `Content-Type`, and any caller passing
      // `credentials` silently drop the session cookie. Nothing does either
      // today because every call goes through `post()`, so the first caller to
      // set a header (an Idempotency-Key, per BUILD_PLAN 8.3) would have got a
      // 422 that reads like a validation bug.
      //
      // Without `credentials` the cookie is not sent at all and every
      // authenticated call 401s while looking like a permissions problem.
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
      ...(signal === undefined ? {} : { signal }),
    });
  } catch {
    // A connection that says it is up and gave no answer is worth a quiet report;
    // a device that knows it is offline, or a deadline that fired, is not.
    if (timedOut?.aborted !== true && !signal?.aborted) {
      notifyApiFailure({ kind: 'network', path });
    }
    // The request may or may not have reached the server: a dropped
    // connection after the commit rejects exactly like no network at all.
    throw new OfflineError(
      timedOut?.aborted === true ? 'the request timed out' : 'no answer arrived',
      timedOut?.aborted === true,
    );
  }

  if (response.status === 204) return undefined as T;

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    // The timer also covers the body: headers that arrive and a body that
    // never finishes is the same dead network, not a malformed response.
    if (timedOut?.aborted === true) throw new OfflineError('the request timed out', true);
    // A 200 that is not JSON is a proxy or a captive portal, not the API. It is
    // treated as a failure rather than parsed hopefully.
    notifyApiFailure({
      kind: 'server',
      status: response.status,
      path,
      code: 'INTERNAL',
      diagnostic: 'BADRSP',
      answered: false,
    });
    if (response.ok) throw new ApiError(response.status, 'INTERNAL', 'BADRSP');
    throw new ApiError(response.status, 'INTERNAL', 'BADRSP');
  }

  if (!response.ok) {
    const envelope = body as ErrorEnvelope;
    const code = (envelope.error?.code ?? 'INTERNAL') as ErrorCode;
    const diagnostic = envelope.error?.diagnostic ?? 'UNKNWN';
    /*
     * `Array.isArray` FIRST, then one entry at a time.
     *
     * `?? []` guards null and undefined and nothing else, so a server (or a
     * proxy, or a captive portal) sending `fields: "nope"` reached `.map` on a
     * string and threw a TypeError out of the very block that exists to turn a
     * bad response into an ApiError. The code and the diagnostic were lost, and
     * the screen showed the generic failure instead of the real one.
     *
     * The comment here used to claim a malformed `fields` could not break this
     * path. It said so while the code did not do it, which is the failure this
     * codebase keeps producing: a comment is not evidence. Caught by the Codex
     * pass and graded P2.
     */
    const raw: unknown = envelope.error?.fields;
    const fields = (Array.isArray(raw) ? (raw as readonly { readonly field?: unknown }[]) : [])
      .map((entry) => entry?.field)
      .filter((field): field is string => typeof field === 'string');
    // The API's handler files its own report for every 5xx it answers, and its envelope carries
    // that report's diagnostic code. A 5xx with no envelope (a proxy, a gateway) never reached it.
    if (response.status >= 500) {
      notifyApiFailure({
        kind: 'server',
        status: response.status,
        path,
        code,
        diagnostic,
        answered: envelope.error?.diagnostic !== undefined,
        followup: response.headers?.get('X-Support-Followup') ?? undefined,
      });
    }
    throw new ApiError(response.status, code, diagnostic, fields);
  }

  return body as T;
}

export const post = <T>(path: string, body: unknown, options: ApiOptions = {}): Promise<T> =>
  api<T>(path, { method: 'POST', body: JSON.stringify(body) }, options);

export const patch = <T>(path: string, body: unknown, options: ApiOptions = {}): Promise<T> =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) }, options);

/**
 * A raw body, such as a photo, sent with the content type it really is.
 *
 * Goes through `api()`, so the error mapping, the offline case and the team
 * funnel are the same as every other call. The JSON content type `api()` sets
 * by default is replaced by `contentType` (its headers merge lets a caller's
 * value win), so the server never sees `application/json` on a JPEG. The reply
 * is still JSON, which is why the response handling needs no second path.
 */
export const putBlob = <T>(
  path: string,
  body: Blob,
  contentType: string,
  options: ApiOptions = {},
): Promise<T> =>
  api<T>(path, { method: 'PUT', body, headers: { 'Content-Type': contentType } }, options);

/**
 * Named `del` because `delete` is a reserved word.
 *
 * No body, deliberately. The server takes the player from the path and the team
 * and season from the membership, so there is nothing for a caller to send and
 * nothing for one to get wrong.
 */
export const del = <T>(path: string, options: ApiOptions = {}): Promise<T> =>
  api<T>(path, { method: 'DELETE' }, options);

/**
 * The field errors a 422 carries, if it carried any.
 *
 * `ApiError` keeps only the code and the diagnostic, because everything else in
 * the app maps a code to themed copy and never reads the server's prose. A form
 * is the one exception: a validation failure names the fields that failed, and
 * a form that cannot mark them makes the person guess which box is wrong.
 *
 * Returned as a map from field name to nothing more than presence. The server's
 * Zod issue codes are not shown to anybody: the FORM owns the wording, because
 * only the form knows what it called the box.
 */
export function fieldErrorsOf(error: unknown): ReadonlySet<string> {
  if (!(error instanceof ApiError)) return EMPTY_FIELDS;
  return error.fields;
}

const EMPTY_FIELDS: ReadonlySet<string> = new Set();
